import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Box, Flex, Heading, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { FiClock, FiUsers, FiFileText } from "react-icons/fi";
import { GET_PUBLIC_GOV_PROPOSAL_TEASER } from "../queries";
import { GET_TENANT_AUTH_CONFIG } from "../../authentication/queries";
import type { PublicGovProposalTeaser as TeaserRow, TenantAuthConfig } from "../../../generated/graphql";
import { GovSurface, GovStatusPill, GovLoading, GovError, FadeIn, useGovTheme, alpha } from "./ui";

/**
 * GovProposalTeaser — what a signed-out visitor sees when a motion link is
 * forwarded to them. Rendered by ProtectedRoute in place of the page; the page
 * itself is never mounted, so nothing from the ballot reaches the DOM.
 *
 * LEAK RULE: this file must never import a page component or a protected query
 * (vault/how-to/seamless-page-auth-pattern.md § Part 3 — written, never clipped).
 * The only data here is `publicGovProposalTeaser`, a whitelisted public read the
 * organisation switches on deliberately (Settings → Link previews). It carries
 * the organisation's name, the title, the status, when voting closes and the
 * opening lines — never the choices, the tally, who voted, or the papers.
 *
 * Design pass 2026-09-12: a hero card in the governance kit — the group's initials tile, the status,
 * when voting closes, the opening lines — sized for a phone first, and readable for every age (16px body,
 * nothing under 14px, no tiny capitals).
 */
export const GOV_PROPOSAL_TEASER = {
  title: "A motion is waiting for you",
  blurb:
    "Sign in to read the full motion, see who proposed it and when voting closes, and cast or delegate your vote if you are a member of the group.",
};

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

export const GovProposalTeaser: React.FC<{ textPrimary?: string; textSecondary?: string }> = ({ textPrimary, textSecondary }) => {
  const { id = "" } = useParams<{ id: string }>();
  const t = useGovTheme();

  const teaserQ = useQuery<{ publicGovProposalTeaser: TeaserRow | null }>(GET_PUBLIC_GOV_PROPOSAL_TEASER, {
    variables: { id }, skip: !id, fetchPolicy: "network-only",
  });
  // Public, tenant-scoped: tells us whether "join free" is a true promise on this site.
  const authQ = useQuery<{ tenantAuthConfig: TenantAuthConfig | null }>(GET_TENANT_AUTH_CONFIG, { fetchPolicy: "cache-first" });
  const signupOpen = Boolean(authQ.data?.tenantAuthConfig?.allowEmailLinkSignup);

  const teaser = teaserQ.data?.publicGovProposalTeaser ?? null;
  const heading = textPrimary || t.text;
  const body = textSecondary || t.muted;

  return (
    <VStack spacing={4} maxW="lg" w="full" data-testid="gov-proposal-teaser" textAlign="left">
      {teaserQ.loading && !teaserQ.data && (
        <Box w="full"><GovLoading label="Looking up the motion…" cards={1} columns={{ base: 1 }} /></Box>
      )}

      {teaserQ.error && (
        <Box w="full">
          <GovError
            title="The preview couldn't be loaded."
            error={`${teaserQ.error.message} — you can still sign in below and the motion will open if you have access.`}
            onRetry={() => teaserQ.refetch()}
          />
        </Box>
      )}

      {!teaserQ.loading && !teaserQ.error && !teaser && (
        <FadeIn>
          <VStack spacing={3} textAlign="center" px={2}>
            <Flex w={14} h={14} borderRadius="2xl" bgImage={t.accentGradient} align="center" justify="center" boxShadow="0 12px 32px rgba(56,158,255,0.35)">
              <Icon as={FiFileText} boxSize={6} color="white" />
            </Flex>
            <Heading size="lg" letterSpacing="-0.02em" color={heading}>{GOV_PROPOSAL_TEASER.title}</Heading>
            <Text fontSize="md" color={body} lineHeight="tall">{GOV_PROPOSAL_TEASER.blurb}</Text>
            <Text fontSize="sm" color={t.faint}>This group hasn't turned on link previews, so the motion's title is shown after you sign in.</Text>
          </VStack>
        </FadeIn>
      )}

      {teaser && (
        <FadeIn style={{ width: "100%" }}>
          <GovSurface w="full" p={{ base: 5, md: 6 }} position="relative" overflow="hidden">
            <Box aria-hidden position="absolute" top="-60px" right="-40px" w="200px" h="160px" bgImage={t.accentGradient}
              opacity={t.colorMode === "dark" ? 0.18 : 0.08} filter="blur(50px)" borderRadius="full" pointerEvents="none" />
            <HStack spacing={3} align="center" position="relative">
              <Flex flexShrink={0} w="44px" h="44px" borderRadius="xl" bgImage={t.accentGradient} align="center" justify="center" color="white" fontWeight="bold" fontSize="sm">
                {initials(teaser.organisationName)}
              </Flex>
              <Box minW={0}>
                <Text fontSize="md" fontWeight="semibold" color={t.muted} wordBreak="break-word">{teaser.organisationName}</Text>
                <HStack spacing={2} mt={1} flexWrap="wrap">
                  <GovStatusPill status={teaser.status} />
                  {teaser.closesAtLabel && (
                    <HStack spacing={1.5} color={t.muted} fontSize="sm">
                      <Icon as={FiClock} boxSize={4} />
                      <Text>closes {teaser.closesAtLabel}</Text>
                    </HStack>
                  )}
                </HStack>
              </Box>
            </HStack>
            <Heading size="md" mt={4} letterSpacing="-0.01em" color={heading} wordBreak="break-word" position="relative">{teaser.title}</Heading>
            {teaser.excerpt && (
              <Text mt={2} fontSize="md" color={body} whiteSpace="pre-wrap" lineHeight="tall" wordBreak="break-word" position="relative">
                {teaser.excerpt}
                <Text as="span" color={t.faint}>…</Text>
              </Text>
            )}
            <HStack mt={4} p={3} spacing={2} borderRadius="xl" bg={alpha(t.primary, 0.08)} align="start" position="relative">
              <Icon as={FiUsers} color={t.primary} mt={0.5} flexShrink={0} />
              <Text fontSize="sm" color={t.muted}>
                {teaser.membersCount} member{teaser.membersCount === 1 ? "" : "s"} decide this. Sign in to read the rest and, if you are one of them, to vote or delegate.
              </Text>
            </HStack>
          </GovSurface>
        </FadeIn>
      )}

      {signupOpen && (
        <Text fontSize="md" color={t.muted} textAlign="center">New here? You can join free when you sign in.</Text>
      )}
    </VStack>
  );
};

export default GovProposalTeaser;
